const SAFE_TAGS = new Set([
  "p",
  "div",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "s",
  "del",
  "strike",
  "code",
  "li",
  "tr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
]);
const DROP_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "svg",
  "math",
  "template",
]);

function createChip(name) {
  const chip = document.createElement("span");
  chip.className = "variable-chip";
  chip.setAttribute("contenteditable", "false");
  chip.setAttribute("data-variable-name", name);
  chip.textContent = name;
  return chip;
}

/**
 * Build only allowlisted DOM; never attach parsed source nodes or attributes.
 *
 * With `allowVariables: false` (a template that has no variables at all) the
 * result never contains a chip: saved chip spans become their plain example
 * text, and neither `{{N}}` placeholders nor known names are turned into chips.
 */
export function hydrateEditor({
  html,
  text = "",
  variables = [],
  identifyNames = false,
  allowVariables = true,
}) {
  const source = new DOMParser().parseFromString(html || "", "text/html").body;
  if (!html) {
    text
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .forEach((line) => {
        const paragraph = document.createElement("p");
        paragraph.textContent = line;
        if (!line) paragraph.appendChild(document.createElement("br"));
        source.appendChild(paragraph);
      });
  }
  const names = variables.filter(
    (name) => typeof name === "string" && name.length,
  );
  const hasChips = source.querySelector(
    ".variable-chip, .highlighted-variable",
  );
  // Name identification is opt-in for the settings identification step. Explicit
  // chips/placeholders are authoritative and must not create extra variables.
  const identify =
    identifyNames && !hasChips && !/\{\{\d+\}\}/.test(source.textContent);
  const namePattern = [...names]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const pattern = new RegExp(
    identify && namePattern ? namePattern : "\\{\\{(\\d+)\\}\\}",
    "g",
  );

  const copy = (node) => {
    const fragment = document.createDocumentFragment();
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent;
      // Explicit chips identify persisted editor HTML, not a wire template.
      // Its other text nodes may intentionally contain literal {{N}} strings.
      if (hasChips || !allowVariables) return document.createTextNode(value);
      let end = 0;
      // A fresh regex per text node avoids shared lastIndex during recursion.
      const matcher = new RegExp(pattern.source, "g");
      let match;
      while ((match = matcher.exec(value))) {
        fragment.appendChild(
          document.createTextNode(value.slice(end, match.index)),
        );
        const name = identify ? match[0] : variables[Number(match[1]) - 1];
        fragment.appendChild(
          typeof name === "string" && name.length
            ? createChip(name)
            : document.createTextNode(match[0]),
        );
        end = matcher.lastIndex;
      }
      fragment.appendChild(document.createTextNode(value.slice(end)));
      return fragment;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return fragment;
    const tag = node.tagName.toLowerCase();
    if (DROP_TAGS.has(tag)) return fragment;
    if (
      tag === "span" &&
      (node.classList.contains("variable-chip") ||
        node.classList.contains("highlighted-variable"))
    ) {
      const name = node.getAttribute("data-variable-name") || node.textContent;
      return allowVariables ? createChip(name) : document.createTextNode(name);
    }
    const target = SAFE_TAGS.has(tag) ? document.createElement(tag) : fragment;
    Array.from(node.childNodes).forEach((child) =>
      target.appendChild(copy(child)),
    );
    return target;
  };
  const result = document.createDocumentFragment();
  Array.from(source.childNodes).forEach((node) =>
    result.appendChild(copy(node)),
  );
  return result;
}
